import type { ExportFileIssue } from '../../types/pcfExport.types';
import { fetchCurrentLicense } from '../currentLicenseClient';
import type {
  LocalPcfCalculationOutcome,
  LocalPcfFile,
} from './pcfExportCalculation';
import type { RunExportFlowOptions } from './pcfExportFlow';
import {
  createExportFailure,
  isUnimplementedError,
  type ExportFlowContext,
  type StepResult,
} from './pcfExportContext';
import { logPcfRunGroupFailures } from './pcfExportDiagnostics';
import {
  PCF_EXPORT_QUOTA_EXHAUSTED_CODE,
  recordLocalPcfRun,
  type PcfRunGrouping,
} from './pcfRunApi';
import { webLog } from './webLogger';

export interface ExportEntitlement {
  readonly isDemo: boolean;
  readonly remainingExportElements: number | null;
}

export interface RecordedRun extends ExportEntitlement {
  readonly runId: string;
  readonly isSuccess: boolean;
}

export interface WriteOutcome {
  readonly filesWritten: number;
  readonly skippedFiles: string[];
  readonly fileIssues: ExportFileIssue[];
  readonly successfulFiles: string[];
  readonly confirmedFiles: LocalPcfFile[];
  readonly elementsWritten: number;
  readonly outputPath: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function responseStatus(error: unknown): number | undefined {
  if (!isRecord(error) || !isRecord(error.response)) {
    return undefined;
  }
  return typeof error.response.status === 'number' ? error.response.status : undefined;
}

function groupingFor(options: RunExportFlowOptions): PcfRunGrouping {
  if (options.groupBy === 'Parameter') {
    return 'ByParameter';
  }
  return options.groupBy === 'SystemTypeName' ? 'BySystemTypeName' : 'BySystemName';
}

export async function refreshExportEntitlement(
  context: ExportFlowContext,
): Promise<StepResult<ExportEntitlement>> {
  try {
    const license = await fetchCurrentLicense();
    if (license.remainingExportElements === 0) {
      return {
        ok: false,
        failure: createExportFailure({
          traceId: context.traceId,
          step: 'license',
          error: 'The export element limit has been reached for this license.',
          extras: {
            errorCode: PCF_EXPORT_QUOTA_EXHAUSTED_CODE,
            quotaExhausted: true,
            isDemo: license.isDemo,
            remainingExportElements: 0,
          },
        }),
      };
    }
    return {
      ok: true,
      value: {
        isDemo: license.isDemo,
        remainingExportElements: license.remainingExportElements,
      },
    };
  } catch (error) {
    const status = responseStatus(error);
    const message = status === 404
      ? 'No active PipeEX license is available. Activate a license and retry.'
      : status === 401 || status === 403
        ? 'Please sign in again — your session has expired.'
        : 'Current license and quota could not be refreshed. Check your connection and retry.';
    return {
      ok: false,
      failure: createExportFailure({
        traceId: context.traceId,
        step: 'license',
        error: message,
      }),
    };
  }
}

export async function recordLocalCalculation(
  context: ExportFlowContext,
  calculation: LocalPcfCalculationOutcome,
  options: RunExportFlowOptions,
  confirmedFiles: readonly LocalPcfFile[],
): Promise<StepResult<RecordedRun>> {
  logPcfRunGroupFailures(context.traceId, calculation.groupFailures);
  webLog.info(
    'export-orchestrator',
    `[pcf-run-record-start] traceId=${context.traceId} runId=${context.runId} `
      + `files=${confirmedFiles.length} elements=${confirmedFiles.reduce((total, file) => total + file.elementCount, 0)} `
      + `groupFailures=${calculation.groupFailures.length}`,
  );
  const recorded = await recordLocalPcfRun({
    runId: context.runId,
    grouping: groupingFor(options),
    elementCount: confirmedFiles.reduce((total, file) => total + file.elementCount, 0),
    filesProduced: confirmedFiles.length,
    groupFailuresCount: calculation.groupFailures.length,
    durationMs: calculation.durationMs,
    pipelineVersion: calculation.pipelineVersion,
    pluginVersion: options.pluginVersion,
    traceId: context.traceId,
    snapshotCacheHit: calculation.snapshotCacheHit,
    ...(confirmedFiles.length === 0
      ? { errorStage: calculation.errorStage || 'pipeline' }
      : {}),
    traceparent: context.traceparent,
  });
  if (!recorded.accepted) {
    webLog.error(
      'export-orchestrator',
      `[pcf-run-record-failed] traceId=${context.traceId} code=${recorded.code ?? 'none'}`,
    );
    return {
      ok: false,
      failure: createExportFailure({
        traceId: context.traceId,
        step: 'record',
        error: recorded.error,
        extras: {
          errorCode: recorded.code,
          // The server uses this code both for a zero balance and for a run
          // larger than a positive balance. Only zero makes future exports
          // impossible; a positive balance remains usable for a smaller run.
          quotaExhausted: recorded.code === PCF_EXPORT_QUOTA_EXHAUSTED_CODE
            && recorded.remainingExportElements === 0,
          quotaInsufficient: recorded.code === PCF_EXPORT_QUOTA_EXHAUSTED_CODE
            && typeof recorded.remainingExportElements === 'number'
            && recorded.remainingExportElements > 0,
          isDemo: recorded.isDemo,
          remainingExportElements: recorded.remainingExportElements,
          filesWritten: 0,
        },
      }),
    };
  }
  if (recorded.isSuccess !== (confirmedFiles.length > 0)) {
    return {
      ok: false,
      failure: createExportFailure({
        traceId: context.traceId,
        step: 'record',
        error: 'The server returned an inconsistent local PCF run receipt.',
        extras: { filesWritten: 0 },
      }),
    };
  }
  webLog.info(
    'export-orchestrator',
    `[pcf-run-record-success] traceId=${context.traceId} runId=${recorded.runId} `
      + `remainingExportElements=${recorded.remainingExportElements ?? 'unlimited'}`,
  );
  return {
    ok: true,
    value: {
      runId: recorded.runId,
      isSuccess: recorded.isSuccess,
      isDemo: recorded.isDemo,
      remainingExportElements: recorded.remainingExportElements,
    },
  };
}

export async function recordFailedCalculation(
  context: ExportFlowContext,
  options: RunExportFlowOptions,
  errorStage = 'stream',
): Promise<void> {
  try {
    const recorded = await recordLocalPcfRun({
      runId: context.runId,
      grouping: groupingFor(options),
      elementCount: 0,
      filesProduced: 0,
      groupFailuresCount: 0,
      durationMs: 0,
      pipelineVersion: options.pluginVersion?.trim() || 'unknown',
      pluginVersion: options.pluginVersion,
      traceId: context.traceId,
      snapshotCacheHit: false,
      errorStage,
      traceparent: context.traceparent,
    });
    if (!recorded.accepted) {
      webLog.warn(
        'export-orchestrator',
        `[pcf-failed-run-record-skipped] traceId=${context.traceId} code=${recorded.code ?? 'none'}`,
      );
    }
  } catch (error) {
    webLog.warn(
      'export-orchestrator',
      `[pcf-failed-run-record-error] traceId=${context.traceId} error=${error instanceof Error ? error.message : String(error)}`,
    );
  }
}

export async function writeFiles(
  context: ExportFlowContext,
  files: LocalPcfFile[],
  outputPath: string,
): Promise<StepResult<WriteOutcome>> {
  webLog.info(
    'export-orchestrator',
    `[pcf-write-start] traceId=${context.traceId} files=${files.length}`,
  );
  try {
    const response = await context.client.writePcfFilesToDisk({
      saveFolder: outputPath,
      files: files.map((file) => ({
        fileName: file.fileName,
        content: file.content,
      })),
    }, {
      headers: { traceparent: context.traceparent },
    });
    const fileIssues = response.issues.map((issue) => ({
      fileName: issue.fileName,
      reason: issue.reason,
      suggestedAction: issue.suggestedAction,
      fatal: issue.fatal,
    }));
    const requestedByName = new Map(files.map((file) => [file.fileName, file]));
    const writtenNames = response.writtenFiles;
    const duplicateWrittenName = writtenNames.find((name, index) => writtenNames.indexOf(name) !== index);
    const unknownWrittenName = writtenNames.find((name) => !requestedByName.has(name));
    if (response.filesWritten !== writtenNames.length || duplicateWrittenName || unknownWrittenName) {
      return {
        ok: false,
        failure: createExportFailure({
          traceId: context.traceId,
          step: 'write',
          error: 'WritePcfFilesToDisk returned an invalid confirmation of written files.',
          extras: {
            filesWritten: response.filesWritten,
            skippedFiles: response.skippedFiles,
            fileIssues,
          },
        }),
      };
    }
    const confirmedFiles = writtenNames.map((fileName) => requestedByName.get(fileName)!);
    const outputPathResolved = response.resolvedSaveFolder.trim();
    if (confirmedFiles.length === 0) {
      return {
        ok: false,
        failure: createExportFailure({
          traceId: context.traceId,
          step: 'write',
          error: response.error || 'No PCF files were confirmed as written to disk.',
          extras: {
            filesWritten: 0,
            skippedFiles: response.skippedFiles,
            fileIssues,
            successfulFiles: [],
            elementsWritten: 0,
            ...(outputPathResolved ? { outputPath: outputPathResolved } : {}),
          },
        }),
      };
    }
    webLog.info(
      'export-orchestrator',
      `[pcf-write-success] traceId=${context.traceId} filesWritten=${response.filesWritten} `
        + `skipped=${response.skippedFiles.length} issues=${fileIssues.length}`,
    );
    return {
      ok: true,
      value: {
        filesWritten: response.filesWritten,
        skippedFiles: response.skippedFiles,
        fileIssues,
        successfulFiles: writtenNames,
        confirmedFiles,
        elementsWritten: confirmedFiles.reduce((total, file) => total + file.elementCount, 0),
        outputPath: outputPathResolved || outputPath,
      },
    };
  } catch (error) {
    const versionMismatch = isUnimplementedError(error);
    return {
      ok: false,
      failure: createExportFailure({
        traceId: context.traceId,
        step: 'write',
        error: versionMismatch
          ? 'This plugin build does not support the new write-back RPC. Update the PipeEX plugin to the latest version.'
          : error instanceof Error
            ? error.message
            : 'WritePcfFilesToDisk failed',
        extras: versionMismatch ? { versionMismatch: true } : {},
      }),
    };
  }
}
